import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ClipboardList } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_STYLE = {
  present: { bg: '#064e3b', border: '#10b981', text: '#a7f3d0', label: 'PRESENT' },
  absent: { bg: '#7f1d1d', border: '#ef4444', text: '#fecaca', label: 'ABSENT' },
  cleared: { bg: '#1f2937', border: '#6b7280', text: '#d1d5db', label: 'CLEARED' },
};

function getTodayEST() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString('en-US', {
    timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', second: '2-digit',
  });
}

export default function AttendanceAuditLog() {
  const [date, setDate] = useState(getTodayEST());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/attendance-audit?date=${date}&_t=${Date.now()}`, { cache: 'no-store' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
      setError(null);
    } catch (e) {
      setError(e.message || 'fetch failed');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const buses = data?.buses || [];

  return (
    <div data-testid="attendance-audit-log" style={{ color: 'white' }}>
      <div style={{ background: '#1f2937', padding: 12, borderRadius: 10, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input
          data-testid="audit-date-input"
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          style={{ padding: 8, fontSize: 14, background: '#111827', color: 'white', border: '1px solid #374151', borderRadius: 6 }}
        />
        <button
          data-testid="audit-refresh-btn"
          onClick={fetchLog}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
        >
          <RefreshCw size={14} className={loading ? 'spinner' : ''} /> Refresh
        </button>
        <div data-testid="audit-total" style={{ fontSize: 12, color: '#9ca3af', marginLeft: 'auto' }}>
          {data ? `${data.total} changes on ${data.date}` : '—'}
        </div>
      </div>

      {error && (
        <div data-testid="audit-fetch-error" style={{ background: '#7f1d1d', padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
          ⚠ Could not load log: {error}
        </div>
      )}

      {!error && buses.length === 0 && (
        <div data-testid="audit-empty" style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
          <ClipboardList size={40} style={{ opacity: 0.5, marginBottom: 10 }} />
          <p style={{ margin: 0, fontSize: 14 }}>No attendance changes recorded for this date.</p>
          <p style={{ margin: '6px 0 0', fontSize: 12 }}>Logging began when the Audit Log feature was deployed — earlier days have no history.</p>
        </div>
      )}

      {buses.map(b => (
        <div key={b.bus_number} data-testid={`audit-bus-${b.bus_number.replace(/\W/g, '')}`}
          style={{ background: '#1f2937', borderRadius: 10, padding: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{b.bus_number}</div>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>{b.entries.length} change{b.entries.length !== 1 ? 's' : ''}</span>
          </div>
          {b.entries.map((e, i) => {
            const s = ACTION_STYLE[e.action] || ACTION_STYLE.cleared;
            return (
              <div key={i} data-testid={`audit-entry-${b.bus_number.replace(/\W/g, '')}-${i}`}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: i < b.entries.length - 1 ? '1px solid #374151' : 'none', fontSize: 13 }}>
                <span style={{ color: '#9ca3af', fontVariantNumeric: 'tabular-nums', minWidth: 92 }}>{fmtTime(e.timestamp)}</span>
                <span style={{ fontWeight: 600, flex: 1 }}>{e.name}</span>
                {e.source === 'external' && (
                  <span style={{ fontSize: 9, fontWeight: 700, color: '#93c5fd', background: '#1e3a8a', padding: '2px 6px', borderRadius: 4 }}>EXTERNAL</span>
                )}
                <span style={{ fontSize: 10, fontWeight: 700, color: s.text, background: s.bg, border: `1px solid ${s.border}`, padding: '2px 8px', borderRadius: 4 }}>
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      ))}
      <style>{`
        .spinner { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
